import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import {
  TotpFlowSessionService,
  TotpPendingCredentials,
} from './totp-flow-session.service';
import { Users } from '../core/model/user';

/** Exact copy of users.login password-expiry message (controllers/users.js). */
const PASSWORD_EXPIRED_MESSAGE =
  'Password expired. Kindly change your password using forgot password options.';

@Injectable({
  providedIn: 'root',
})
export class TotpAuthRoutingService {
  constructor(
    private router: Router,
    private authService: AuthService,
    private totpFlow: TotpFlowSessionService,
  ) {}

  routeAfterValidateLogin(
    res: Users,
    returnUrl: string,
    credentials?: TotpPendingCredentials,
  ): boolean {
    if (res?.totpEnrollmentRequired) {
      // Parity with MFA: mark before /auth/mfa so a second tab gets Active Session.
      this.markLoginSessionActive();
      this.totpFlow.begin(credentials);
      this.authService.applyPortalTheme(res?.themeColor);
      // Do not replaceUrl — keep /auth/login under TOTP so browser Back returns to login (IP-9544).
      this.router.navigateByUrl('/auth/totp/setup');
      return true;
    }
    if (res?.totpVerificationRequired) {
      this.markLoginSessionActive();
      this.totpFlow.begin(credentials);
      this.authService.applyPortalTheme(res?.themeColor);
      this.router.navigateByUrl('/auth/totp/verify');
      return true;
    }
    return false;
  }

  /**
   * Same marker MFA sets before navigating to /auth/mfa.
   * Login checks this and shows the Active Session modal.
   */
  markLoginSessionActive(): void {
    localStorage.setItem('logger_token', 'multiLogin');
  }

  /**
   * Clear only when abandoning TOTP without a completed login (Back / password-expired).
   * Do not clear from cold guards — another tab may be fully signed in.
   */
  clearLoginSessionMarker(): void {
    localStorage.removeItem('logger_token');
  }

  /**
   * Leave TOTP/MFA after unrecoverable errors (lock, expiry, session gone).
   * SPA navigation — faster than full reload; session state already cleared in memory.
   */
  returnToLoginAfterAbandon(): void {
    this.clearLoginSessionMarker();
    this.totpFlow.clear();
    this.authService.clearPendingAuthUser();
    this.router.navigateByUrl('/auth/login', { replaceUrl: true });
  }

  guardRedirect(user: Users | null, stateUrl: string): boolean {
    if (!user) return false;

    const needsTotp =
      !!user.totpVerificationRequired
      || !!user.totpEnrollmentRequired
      || !!user.totpEnrollmentPending;

    if (!needsTotp) {
      return false;
    }

    // Cold / storage-only mid-flow: do not reopen TOTP URLs — silent login.
    if (!this.totpFlow.isActive) {
      this.authService.clearPendingAuthUser();
      this.router.navigate(['/auth/login']);
      return true;
    }

    if (user.totpVerificationRequired) {
      this.router.navigate(['/auth/totp/verify'], { replaceUrl: true });
      return true;
    }
    if (user.totpEnrollmentRequired || user.totpEnrollmentPending) {
      this.router.navigate(['/auth/totp/setup'], { replaceUrl: true });
      return true;
    }
    return false;
  }

  navigateAfterLogin(res: Users, returnUrl: string): void {
    this.totpFlow.clear();
    localStorage.setItem('logger_token', 'multiLogin');

    if (res?.userType === 'MULTI_STORE_MANAGER') {
      this.navigateToReturnUrl(returnUrl || '/user-management/users');
      return;
    }

    const excludeFeatures = res?.excludeFeatures || [];
    const roleFeatures = res?.roleFeatures || [];
    const featureExists = (module: string, feature: string) =>
      roleFeatures.some(
        (item) => item.module === module && (item.features || []).includes(feature)
      );

    if (excludeFeatures.indexOf('VIEW_DASHBOARD') > -1) {
      this.router.navigateByUrl(
        featureExists('CLOUD_POS', 'PAYMENTS')
          ? '/virtual-terminal/list'
          : featureExists('TRANSACTION', 'VIEW_TRANSACTION')
            ? '/transaction-management/transactions'
            : '/user-management/users'
      );
    } else {
      this.navigateToReturnUrl(returnUrl || '/');
    }
  }

  isPasswordExpiredError(err: any): boolean {
    const message = String(err?.message || '');
    return message === PASSWORD_EXPIRED_MESSAGE
      || message.toLowerCase().includes('password expired');
  }

  /**
   * After modal OK, leave TOTP for unrecoverable errors (all user types):
   * Prefer BE navigateToLogin / Java mfaErrorCode (stable); message text is fallback only.
   * Wrong codes stay on the page. Password expiry uses a separate path.
   */
  shouldNavigateToLoginAfterError(err: any): boolean {
    if (err?.navigateToLogin === true) {
      return true;
    }
    if (this.isPasswordExpiredError(err)) {
      return false;
    }
    const code = String(err?.mfaErrorCode || '').toUpperCase();
    if (
      code === 'RATE_LIMIT_EXCEEDED'
      || code === 'MFA_SETUP_EXPIRED'
      || code === 'TOTP_SESSION_EXPIRED'
      || code === 'ACCOUNT_LOCKED'
    ) {
      return true;
    }
    if (Number(err?.status) === 429) {
      return true;
    }
    const message = String(err?.message || '').toLowerCase();
    if (message.includes('locked') || (message.includes('account') && message.includes('lock'))) {
      return true;
    }
    if (message.includes('too many setup')) {
      return true;
    }
    if (!message.includes('expir')) {
      return false;
    }
    return true;
  }

  /**
   * After TOTP, login() may reject with password-expired (same as legacy MFA after OTP).
   * Return to login via SPA navigation, restore stashed credentials, show the same modal.
   */
  returnToLoginAfterPasswordExpired(err: any): void {
    this.clearLoginSessionMarker();
    this.totpFlow.endFlowKeepCredentials();
    this.authService.clearPendingAuthUser();
    this.totpFlow.setPendingLoginError({
      message: err?.message || PASSWORD_EXPIRED_MESSAGE,
      type: err?.type,
    });
    this.router.navigateByUrl('/auth/login', { replaceUrl: true });
  }

  private navigateToReturnUrl(url: string): void {
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      window.location.href = url;
    } else {
      this.router.navigateByUrl(url || '/');
    }
  }
}
